/**
 * @fileoverview S3 image upload helpers for custom notifications.
 * @module pages/notificationForm/notificationFormImageUpload
 */
import { v4 as uuidv4 } from "uuid";
import { getImagePresignedUrl, uploadToS3 } from "../../logic/upload/index.js";
import {
  NOTIFICATION_TYPE_EMAIL,
  NOTIFICATION_TYPE_APP,
  NOTIFICATION_TYPE_WHATSAPP,
} from "./notificationFormConstants.js";

/**
 * Uploads notification image to S3 when required, reusing cached payload when possible.
 * @returns {Promise<{ success: boolean, imagePayload?: Object, error?: string }>}
 */
export const uploadNotificationImageIfNeeded = async ({
  formData,
  imageFile,
  newObjectId,
  setNewObjectId,
  pendingUploadedImageRef,
  navigate,
}) => {
  const tipo = formData.tipo?.value;
  if (tipo !== NOTIFICATION_TYPE_EMAIL && tipo !== NOTIFICATION_TYPE_WHATSAPP) {
    return { success: true, imagePayload: null };
  }

  if (!imageFile) {
    return { success: true, imagePayload: null };
  }

  const fileSignature = `${imageFile.name}:${imageFile.size}:${imageFile.lastModified}`;
  const cached = pendingUploadedImageRef.current;
  if (
    cached?.fileSignature === fileSignature &&
    cached?.imagePayload?._id &&
    cached?.imagePayload?.imagenUrl
  ) {
    return { success: true, imagePayload: cached.imagePayload };
  }

  const objectId = newObjectId || uuidv4();
  if (!newObjectId) {
    setNewObjectId(objectId);
  }

  const presigned = await getImagePresignedUrl(
    { objectId, s3Path: "customNotifications/images" },
    navigate,
  );
  if (!presigned.success) {
    return { success: false, error: presigned.message };
  }

  await uploadToS3(presigned.data.uploadUrl, imageFile);
  const imagePayload = {
    _id: objectId,
    imagenUrl: `customNotifications/images/${objectId}`,
  };
  pendingUploadedImageRef.current = { fileSignature, imagePayload };
  return { success: true, imagePayload };
};

/** Builds the API payload for the selected notification type. */
export const buildNotificationPayload = (formData, imagePayload) => {
  const tipo = formData.tipo?.value;
  const base = {
    ...formData,
    tipo: formData.tipo ? formData.tipo.value : null,
  };

  if (tipo === NOTIFICATION_TYPE_EMAIL || tipo === NOTIFICATION_TYPE_WHATSAPP) {
    return { ...base, ...imagePayload };
  }
  if (tipo === NOTIFICATION_TYPE_APP) {
    return base;
  }
  return null;
};
